import React, { useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';

import { useAuth } from '../../../contexts/AuthContext';
import logger from '../../../logging/logger';
import type { MarketMonitorDashboardState } from '../dashboards/MarketMonitorDashboardProvider';
import { deleteDashboardSchedule, getDashboardSchedule } from './scheduleApi';
import {
  RemoveScheduleModal,
  type RemoveScheduleState,
} from './RemoveScheduleModal';
import styles from './RemoveScheduleButton.module.css';

type Toast =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string }
  | null;

export interface RemoveScheduleButtonProps {
  state: MarketMonitorDashboardState;
}

const errorMessage = (caught: unknown): string =>
  caught instanceof Error ? caught.message : 'Unexpected error.';

export const RemoveScheduleButton: React.FC<RemoveScheduleButtonProps> = ({ state }) => {
  const { userUuid, getToken } = useAuth();
  const { activeId, isSaving, isLoadingActive } = state;

  const [open, setOpen] = useState<boolean>(false);
  const [modalState, setModalState] = useState<RemoveScheduleState>({ phase: 'loading' });
  const [toast, setToast] = useState<Toast>(null);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(id);
  }, [toast]);

  const loadSchedule = useCallback(
    async (dashboardId: string) => {
      setModalState({ phase: 'loading' });
      try {
        if (!userUuid) {
          throw new Error('Sign in is required.');
        }
        const token = await getToken();
        const schedule = await getDashboardSchedule(userUuid, token, dashboardId);
        if (!schedule) {
          setModalState({ phase: 'empty' });
          return;
        }
        setModalState({
          phase: 'loaded',
          schedule,
          deleting: false,
          deleteError: null,
        });
      } catch (caught: unknown) {
        const message = errorMessage(caught);
        logger.error(`RemoveScheduleButton: load failed — ${message}`);
        setModalState({ phase: 'loadError', message });
      }
    },
    [userUuid, getToken],
  );

  const handleClick = useCallback(() => {
    if (!userUuid) {
      setToast({ kind: 'error', message: 'Sign in is required to manage schedules.' });
      return;
    }
    if (!activeId) {
      setToast({ kind: 'error', message: 'Save the dashboard before removing its schedule.' });
      return;
    }
    setOpen(true);
    void loadSchedule(activeId);
  }, [userUuid, activeId, loadSchedule]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleRetry = useCallback(() => {
    if (!activeId) return;
    void loadSchedule(activeId);
  }, [activeId, loadSchedule]);

  const handleRemove = useCallback(async () => {
    if (modalState.phase !== 'loaded') return;
    if (!userUuid || !activeId) return;
    const schedule = modalState.schedule;
    setModalState({ phase: 'loaded', schedule, deleting: true, deleteError: null });
    try {
      const token = await getToken();
      const deleted = await deleteDashboardSchedule(userUuid, token, activeId);
      if (!deleted) {
        setModalState({ phase: 'empty' });
        return;
      }
      setModalState({ phase: 'removed' });
      setToast({ kind: 'success', message: 'Schedule removed.' });
    } catch (caught: unknown) {
      const message = errorMessage(caught);
      logger.error(`RemoveScheduleButton: delete failed — ${message}`);
      setModalState({
        phase: 'loaded',
        schedule,
        deleting: false,
        deleteError: message,
      });
    }
  }, [modalState, userUuid, activeId, getToken]);

  const buttonDisabled = isSaving || isLoadingActive;

  return (
    <>
      <button
        type="button"
        className={styles.button}
        onClick={handleClick}
        disabled={buttonDisabled}
        title="Remove the recurring email schedule for this dashboard"
        data-testid="remove-schedule-button"
      >
        <Trash2 size={14} aria-hidden="true" />
        <span>Remove schedule</span>
      </button>

      <RemoveScheduleModal
        open={open}
        state={modalState}
        onClose={handleClose}
        onRemove={() => {
          void handleRemove();
        }}
        onRetry={handleRetry}
      />

      {toast ? (
        <div
          className={`${styles.toast} ${toast.kind === 'success' ? styles.toastSuccess : styles.toastError}`}
          role="status"
          data-testid={`remove-schedule-toast-${toast.kind}`}
        >
          {toast.message}
        </div>
      ) : null}
    </>
  );
};
